import { Suspense } from "react";
import { useParams } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { EcosuiteErrorBoundary } from "@/components/error-boundary";
import { LoadingScreen } from "@/components/ui/loading-screen";
import RootUserLayout from "@/features-root-user/layout";
import { TOP_NAV } from "../constants/top-nav";
import {
  organizationQueryOptions,
  userCountsQueryOptions,
  organizationUsersQueryOptions,
  allOrganizationsOnboardingCountsQueryOptions,
} from "@/features-root-user/service/organization";
import { UserCheck, UserCog, Users } from "lucide-react";

// Import components
import {
  OrganizationInfoCard,
  UserStatsCards,
  ChartsSection,
  OrganizationUsersTable,
} from "./components";

// Import types
import { OrganizationUser, UserTypeChartData } from "./types";
import { CHART_COLORS } from "./constants";

/**
 * Main content component for organization details
 */
function OrganizationDetailContent() {
  const { id } = useParams({
    from: "/_root/root-user/organization/organization/$id/detail",
  });

  // Fetch organization data using React Query suspense hooks
  const { data: organization } = useSuspenseQuery(organizationQueryOptions(id));
  const { data: userCounts } = useSuspenseQuery(userCountsQueryOptions(id));
  const { data: organizationUsers = [] } = useSuspenseQuery(
    organizationUsersQueryOptions(id),
  );
  const { data: onboardingCounts } = useSuspenseQuery(
    allOrganizationsOnboardingCountsQueryOptions,
  );

  // Get onboarding users count for this organization
  const onboardingUsersCount = onboardingCounts?.[id] || 0;

  // Group users by user type
  const userTypeGroups = organizationUsers.reduce<
    Record<string, OrganizationUser[]>
  >((acc, user) => {
    const typeId = user.userTypeId || "unknown";
    if (!acc[typeId]) {
      acc[typeId] = [];
    }
    acc[typeId].push(user);
    return acc;
  }, {});

  // Prepare data for user type chart
  const userTypeChartData: UserTypeChartData[] = Object.entries(
    userTypeGroups,
  ).map(([typeId, users], index) => {
    // Use the first user's userTypeName if available, otherwise extract from ID
    const typeName =
      users[0]?.userTypeName ||
      typeId
        .substring(typeId.lastIndexOf("-") + 1, typeId.length)
        .substring(0, 12);

    return {
      name: typeName,
      size: users.length,
      color: CHART_COLORS[index % CHART_COLORS.length],
      users,
    };
  });

  // Create data for statistics
  const statsData = [
    {
      name: "Total Users",
      value: userCounts?.total || 0,
      icon: <Users className="h-4 w-4 text-indigo-500" />,
    },
    {
      name: "Active Users",
      value: userCounts?.active || 0,
      icon: <UserCheck className="h-4 w-4 text-emerald-500" />,
    },
    {
      name: "Onboarding Users",
      value: onboardingUsersCount,
      icon: <UserCog className="h-4 w-4 text-amber-500" />,
    },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      {/* Organization Information Card */}
      <OrganizationInfoCard organization={organization} />

      {/* User Statistics Cards */}
      <UserStatsCards statsData={statsData} />

      {/* Charts Section */}
      <ChartsSection
        userTypeChartData={userTypeChartData}
        statsData={statsData}
        userCounts={userCounts}
      />

      {/* Organization Users Table */}
      <OrganizationUsersTable
        organizationUsers={organizationUsers}
        userTypeGroups={userTypeGroups}
      />
    </div>
  );
}

/**
 * Main organization detail page component
 */
export default function OrganizationDetail() {
  return (
    <RootUserLayout
      pageTitle="Organization Details"
      pageDescription="View and manage organization details."
      topNav={TOP_NAV}
    >
      <Suspense
        fallback={
          <LoadingScreen
            text="Loading Organization..."
            className="h-[calc(100vh-180px)]"
          />
        }
      >
        <EcosuiteErrorBoundary>
          <OrganizationDetailContent />
        </EcosuiteErrorBoundary>
      </Suspense>
    </RootUserLayout>
  );
}
